import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Typography } from '@material-ui/core';

import { Location, locationName, UserRole } from '@cobliteam/common-types';
import type { Preferences as LocalPreferences } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';
import { CookieManager } from '@cobliteam/cookie-manager';
import Preferences from '@cobliteam/preferences';
import { useScrollRef } from '@cobliteam/tracking/src/useScrollRef';

import { type AppState, selectLocationPathname } from '@cobliteam/store';

import { useCrm, useLocalStorage, useUser } from '@cobliteam/hooks';
import { useUpdateVersion } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

import { LOW_RESOLUTION_COOKIE_NAME, LowResolutionPlaceholder } from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import NavbarDashboard from '../../components/NavbarDashboard';
import SidebarContainer from '../../components/Sidebar';
import StorybookLink from '../../components/StorybookLink';

import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
import { configureAnalytics, configureClarity, configureDatadogRUM, configureNps, configureOneSignal, configureSentry } from '../../libs/configureThirdParty';
import LoadingPage from '../../pages/LoadingPage';

const PermissionDX = lazy(() => import('@cobliteam/hooks/src/useDX/usePermissions'));

const convertLocationToRegexPattern = (inputString: string): RegExp => {
  const escapedString = inputString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexPattern = escapedString.replace(/:[^/]+/g, '[^/]+');
  return new RegExp('^' + regexPattern + '$');
};

const getLocationName = (location: string): string => {
  const locationsArray = Object.values(Location);

  for (const loc of locationsArray) {
    if (loc === location) {
      return locationName[loc];
    }

    const regex = convertLocationToRegexPattern(loc);
    if (regex.test(location)) {
      return locationName[loc];
    }
  }

  return 'não encontrada';
};

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  // FORCE UPDATE VERSION
  useUpdateVersion();
  const history = useHistory();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const currentLocation = useSelector((state: AppState) => selectLocationPathname(state));
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum } = useFeatureFlags();

  const { getSession, useGetUserData } = useUser();
  const session = getSession();
  const { data: user } = useGetUserData();

  const { useGetDealIdHubspot } = useCrm();
  const { data: deal, isFetching: dealIsFetching } = useGetDealIdHubspot();

  const { scrollableDivRef } = useScrollRef();

  const [shouldContinue, setShouldContinue] = useState(CookieManager.getCookie(LOW_RESOLUTION_COOKIE_NAME) === 'true');

  const hasMapUsage = currentLocation === Location.ROUTING;

  const isLocalhost = window.location.hostname.includes('localhost');
  const isPreview = window.location.hostname.includes('preview');
  const isDev = isLocalhost || isPreview;

  // TEMPORARY
  const [localStorage, setLocalStorage] = useLocalStorage<LocalPreferences>('preferences');
  const { data: userData } = useUser().useGetUserData();
  const { showWarningBanner } = useFeatureFlags();

  const configureThirdPartyServices = useCallback(() => {
    configureClarity(session);
    configureSentry(session);

    if (user?.userAttributes && !dealIsFetching) {
      configureNps(session, user?.userAttributes, deal);
    }

    if (session.user.role !== UserRole.MAP_VIEW && oneSignal) {
      configureOneSignal(session);
    }

    if (useDdRum && ddRumSampleRate) {
      const sampleRate = ddRumSampleRate;
      const replaySampleRate = ddRumReplaySampleRate || 0;
      const userAttributes = user?.userAttributes;
      configureDatadogRUM(session, sampleRate, replaySampleRate, userAttributes);
    }
  }, [session, user?.userAttributes, dealIsFetching, oneSignal, useDdRum, ddRumSampleRate, ddRumReplaySampleRate, deal]);

  const continueToDashboard = () => {
    CookieManager.setCookie(LOW_RESOLUTION_COOKIE_NAME, 'true');
    setShouldContinue(true);
  };

  const configAnalytics = useCallback(() => {
    configureAnalytics(session);
  }, [session]);

  useEffect(() => {
    configAnalytics();
  }, [configAnalytics]);

  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  useEffect(() => {
    const urlToRedirect = redirectSessionStorage();
    if (urlToRedirect) history.push(urlToRedirect);
  }, [history, redirectSessionStorage]);

  useEffect(() => {
    configureThirdPartyServices();
  }, [configureThirdPartyServices]);

  useEffect(() => Analytics.pageview(locationName[currentLocation]), [currentLocation]);

  useEffect(() => {
    if (localStorage?.showIncidentBanner === undefined) {
      setLocalStorage({ ...localStorage, showIncidentBanner: true });
    }
  }, [localStorage, setLocalStorage]);

  // is onboarding path, do not render layout
  if (currentLocation === Location.ONBOARDING_SETUP) return null;

  if (!shouldContinue && window.innerWidth <= 720) return <LowResolutionPlaceholder onContinue={continueToDashboard} />;

  const contentClassName = `${hasMapUsage ? '' : 'scrollable'}`;

  return (
    <div>
      {isDev && (
        <Suspense fallback='Carregando Permission DX'>
          <PermissionDX />
          <StorybookLink />
        </Suspense>
      )}
      <div id='page-body'>
        <div id='modals' />
        <div id='map-background' className={hasMapUsage ? undefined : 'hidden'} />
        <div id='container'>
          <SidebarContainer />
          <div id='main-content'>
            <NavbarDashboard />
            {userData?.hasCameraFlag && showWarningBanner && localStorage?.showIncidentBanner ? (
              <div className='pointer-events-auto absolute top-0 z-50 box-border flex w-full items-center gap-2.5 bg-warning px-4 py-2.5'>
                <WarningRoundedIcon className='h-6 w-6 text-black' />
                <Typography variant='body2' className='font-medium text-black'>
                  Estamos enfrentando indisponibilidade em relação a ingestão de dados de câmera, atraso no monitoramento em tempo real é esperado, bem como na apresentação dos
                  trajetos, entretanto, eventos de vídeo devem chegar normalmente.
                </Typography>
                <Button
                  variant='ghost'
                  size='condensed'
                  color='secondary'
                  icon={<CloseRoundedIcon className='h-6 w-6' />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocalStorage({
                      ...localStorage,
                      showIncidentBanner: false,
                    });
                  }}
                />
              </div>
            ) : null}
            {/* map-background has pointer events conflict with content  */}
            <div id='content' className={contentClassName} ref={scrollableDivRef}>
              <Suspense fallback={<LoadingPage message={`Carregando página de ${getLocationName(currentLocation)?.toLocaleLowerCase() || 'não encontrada'}...`} />}>
                {children}
              </Suspense>
            </div>
          </div>
        </div>
      </div>
      <div id='page-components' />
    </div>
  );
};

export default AppLayout;
